import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Copy, Share2, ClipboardCopy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, errMsg } from "@/lib/api";
import { SIZES, THEMES, drawCard, loadImage, quoteText } from "@/lib/quoteCard";

const Toggle = ({ id, label, checked, onChange, disabled }) => (
  <label className="flex items-center justify-between gap-3 text-sm text-[#D6CCBC]">{label}<Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={`share-toggle-${id}`} /></label>
);

const Pick = ({ id, label, value, onChange, options }) => (
  <label className="block text-xs text-[#A8A090]">{label}
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid={`share-${id}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`share-${id}-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

const toBlob = (c) => new Promise((r) => c.toBlob(r, "image/png"));

export const shareLink = (id) => `${API}/s/${id.slice(0, 8)}`;

export const ShareQuoteDialog = ({ quote: base, uid, onClose, defaultStoryId = "none", editableSpeaker = false }) => {
  const canvas = useRef(null);
  const [size, setSize] = useState("square");
  const [theme, setTheme] = useState("midnight");
  const [storyId, setStoryId] = useState(defaultStoryId);
  const [speaker, setSpeaker] = useState(base.lines[0]?.speaker_name || "");
  const quote = useMemo(() => (editableSpeaker ? { ...base, lines: [{ ...base.lines[0], speaker_name: speaker.trim() }] } : base), [base, editableSpeaker, speaker]);
  const [useCover, setUseCover] = useState(false);
  const [show, setShow] = useState({ speaker: true, universe: true, mark: true });
  const [imgs, setImgs] = useState({});
  const { data: uni } = useQuery({ queryKey: ["universe", uid], queryFn: () => api.get(`/universes/${uid}`).then((r) => r.data) });
  const { data: stories = [] } = useQuery({ queryKey: ["shareable-stories", uid], queryFn: () => api.get(`/universes/${uid}/shareable-stories`).then((r) => r.data) });
  const story = stories.find((s) => s.id === storyId);
  const url = story ? shareLink(story.id) : "";
  const universeName = uni?.name || "";

  useEffect(() => { loadImage("/logo.png").then((logo) => setImgs((m) => ({ ...m, logo }))).catch(() => {}); }, []);
  useEffect(() => {
    if (!story?.has_cover || imgs[story.id]) return;
    api.get(`/stories/${story.id}/cover`, { responseType: "blob" }).then((r) => loadImage(URL.createObjectURL(r.data))).then((img) => setImgs((m) => ({ ...m, [story.id]: img }))).catch(() => {});
  }, [story, imgs]);
  const cover = useCover && story?.has_cover ? imgs[story.id] : null;
  useEffect(() => {
    if (canvas.current) drawCard(canvas.current, { quote, size, theme, cover, show, universeName, story, url, logo: imgs.logo });
  }, [quote, size, theme, cover, show, universeName, story, url, imgs.logo]);

  const caption = `${quoteText(quote).join("\n")}${quote.lines.length === 1 && quote.lines[0].speaker_name ? `\n— ${quote.lines[0].speaker_name}` : ""}${story ? `\n\nRead “${story.title}”: ${url}` : ""}`;
  const fileName = `quote-${size}.png`;
  const download = async () => { const a = document.createElement("a"); a.href = URL.createObjectURL(await toBlob(canvas.current)); a.download = fileName; a.click(); };
  const copyImage = async () => { try { await navigator.clipboard.write([new window.ClipboardItem({ "image/png": toBlob(canvas.current) })]); toast.success("Image copied"); } catch { toast.error("Your browser can't copy images. Use Download instead."); } };
  const copyCaption = async () => { try { await navigator.clipboard.writeText(caption); toast.success("Caption copied"); } catch (e) { toast.error(errMsg(e)); } };
  const share = async () => {
    const file = new File([await toBlob(canvas.current)], fileName, { type: "image/png" });
    const data = { files: [file], text: caption, ...(url ? { url } : {}) };
    if (navigator.canShare?.(data)) { try { await navigator.share(data); } catch { /* cancelled */ } }
    else { await download(); await copyCaption(); toast.info("Sharing isn't supported here. The image was downloaded and the caption copied."); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="share-quote-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Share as image</DialogTitle></DialogHeader>
        <div className="grid gap-6 md:grid-cols-[1fr_280px]">
          <div className="flex items-center justify-center rounded-md bg-[#0B0D10] p-4"><canvas ref={canvas} className="max-h-[60vh] max-w-full rounded shadow-[0_10px_40px_rgba(0,0,0,0.5)]" style={{ width: "auto", height: "auto" }} data-testid="share-canvas" /></div>
          <div className="space-y-4">
            {editableSpeaker && <label className="block text-xs text-[#A8A090]">Speaker (optional)<Input value={speaker} onChange={(e) => setSpeaker(e.target.value)} placeholder="e.g. Kael" maxLength={80} className="mt-1" data-testid="share-speaker-input" /></label>}
            <Pick id="size" label="Size" value={size} onChange={setSize} options={Object.entries(SIZES).map(([k, v]) => [k, v[2]])} />
            <Pick id="theme" label="Theme" value={theme} onChange={setTheme} options={Object.entries(THEMES).map(([k, v]) => [k, v.name])} />
            <Pick id="story" label="Link to a published story" value={story ? storyId : "none"} onChange={(v) => { setStoryId(v); if (v === "none") setUseCover(false); }} options={[["none", "No link"], ...stories.map((s) => [s.id, s.title])]} />
            {stories.length === 0 && <p className="text-xs text-[#A8A090]" data-testid="share-no-stories">Share a story by link or list it on More Universes to add a link here.</p>}
            <Toggle id="cover" label="Use story cover as background" checked={useCover} onChange={setUseCover} disabled={!story?.has_cover} />
            <Toggle id="speaker" label="Speaker name" checked={show.speaker} onChange={(v) => setShow({ ...show, speaker: v })} disabled={quote.lines.length > 1} />
            <Toggle id="universe" label="Universe name" checked={show.universe} onChange={(v) => setShow({ ...show, universe: v })} />
            <Toggle id="mark" label="LoreRight mark" checked={show.mark} onChange={(v) => setShow({ ...show, mark: v })} />
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button onClick={download} data-testid="share-download"><Download className="mr-1 h-4 w-4" />Download</Button>
              <Button variant="outline" onClick={share} data-testid="share-native"><Share2 className="mr-1 h-4 w-4" />Share</Button>
              <Button variant="outline" onClick={copyImage} data-testid="share-copy-image"><Copy className="mr-1 h-4 w-4" />Copy image</Button>
              <Button variant="outline" onClick={copyCaption} data-testid="share-copy-caption"><ClipboardCopy className="mr-1 h-4 w-4" />Copy caption</Button>
            </div>
            {url && <p className="break-all text-xs text-[#A8A090]" data-testid="share-url">{url}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
